import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/en';
import 'dayjs/locale/es';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { enUS, esES } from '@mui/x-date-pickers/locales';

import {
  SEARCH_DATE_FORMAT,
  isSearchDate,
  searchDateDisplayFormat,
  searchDateLocale,
} from '../searchDates';

function pickerDateValue(value: string | undefined): Dayjs | null {
  return isSearchDate(value) ? dayjs(value) : null;
}

function pickerDateParam(value: Dayjs | null): string {
  return value?.isValid() ? value.format(SEARCH_DATE_FORMAT) : '';
}

interface Props {
  label: string;
  value: string;
  onChange: (value: string) => void;
  minDate?: string;
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  open?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  onAccept?: () => void;
}

/**
 * DatePicker Community común para Rutero. La UI trabaja con Dayjs y locale,
 * mientras que formularios, URL y payload conservan siempre YYYY-MM-DD.
 */
export function SearchDatePicker({
  label,
  value,
  onChange,
  minDate,
  required,
  disabled,
  error,
  helperText,
  open,
  onOpen,
  onClose,
  onAccept,
}: Props) {
  const { i18n } = useTranslation();
  const locale = searchDateLocale(i18n.resolvedLanguage ?? i18n.language);
  const { localeText } = (locale === 'es' ? esES : enUS)
    .components.MuiLocalizationProvider.defaultProps;
  const [pickerValue, setPickerValue] = useState<Dayjs | null>(
    () => pickerDateValue(value),
  );

  useEffect(() => {
    if (pickerDateParam(pickerValue) !== value) {
      setPickerValue(pickerDateValue(value));
    }
  }, [pickerValue, value]);

  const handleChange = (nextValue: Dayjs | null) => {
    setPickerValue(nextValue);
    onChange(pickerDateParam(nextValue));
  };

  return (
    <LocalizationProvider
      dateAdapter={AdapterDayjs}
      adapterLocale={locale}
      localeText={localeText}
    >
      <DatePicker
        label={label}
        value={pickerValue}
        onChange={handleChange}
        onAccept={(nextValue) => {
          if (nextValue?.isValid()) onAccept?.();
        }}
        minDate={pickerDateValue(minDate) ?? undefined}
        format={searchDateDisplayFormat(locale)}
        disabled={disabled}
        open={open}
        onOpen={onOpen}
        onClose={onClose}
        slotProps={{
          textField: {
            size: 'small',
            fullWidth: true,
            required,
            error: error || undefined,
            helperText,
          },
        }}
      />
    </LocalizationProvider>
  );
}
